import { Box, Container } from "@chakra-ui/react";
import { GetStaticProps } from "next";
import { useRouter } from "next/dist/client/router";
import React, { useCallback, useEffect, useState } from "react";
import {
  IWorkshopWithMentorModel,
  IMentorModel,
  DB_CATEGORIES,
  mapDBCategoryToOption,
  mapDBCategoriesToOptions,
  Option,
} from "ui";
import { PageContainer } from "../src/components/ui/PageContainer";
import { CustomSelect } from "../src/components/ui/Select";
import { WorkshopFilter } from "../src/components/WorkshopFilter/WorkshopFilter";
import { WorkshopGrid } from "../src/components/WorkshopGrid";

import {
  getFilteredWorkshopsWithMentor,
  getWorkshops,
  workshopsWithMentor,
} from "../src/utils/firebase";
import { IWorkshopModel } from "ui";
import { HeroWrapper } from "../src/components/ContentPages/HeroWrapper";
import { Layout } from "../src/components/Layout";

interface IWorkshopsPage {
  allWorkshopsWithMentor: IWorkshopWithMentorModel[];
}

type WorkshopWithMentor = IWorkshopModel & { mentor: IMentorModel };

const Offers: React.FC<IWorkshopsPage> = ({ allWorkshopsWithMentor }) => {
  const [selectedCategories, setSelectedCategories] = useState<
    string[] | string
  >();
  const onSetSelectedCategories = (selectedCategories: string[] | string) => {
    setSelectedCategories(selectedCategories);
  };
  const [filteredWorkshops, setFilteredWorkshops] = useState<
    WorkshopWithMentor[] | undefined
  >(undefined);
  const router = useRouter();
  const { query: urlWorkshopsQuery } = router;
  const categories = DB_CATEGORIES;
  const categoriesAsOptions = mapDBCategoriesToOptions(categories);

  useEffect(() => {
    async function onGetFilteredWorkshopsWithMentor() {
      // TODO: [] update function to handle undefined url query as param
      // TODO: [] update function to also handle hashtag(s)
      if (!urlWorkshopsQuery.category) {
        setFilteredWorkshops(undefined);
      }

      let singleCategoryAsArray: string[] = [];
      if (typeof urlWorkshopsQuery.category === "string") {
        singleCategoryAsArray.push(urlWorkshopsQuery.category);
      }
      const filteredWorkshopsWithMentor = await getFilteredWorkshopsWithMentor({
        type: "category",
        query:
          singleCategoryAsArray.length > 0
            ? singleCategoryAsArray
            : urlWorkshopsQuery.category,
        onlyActive: true,
      });

      setFilteredWorkshops(filteredWorkshopsWithMentor);
    }

    if (
      !(
        urlWorkshopsQuery === null &&
        urlWorkshopsQuery === undefined &&
        Object.keys(urlWorkshopsQuery).length === 0
      )
    ) {
      onGetFilteredWorkshopsWithMentor();
    } else {
      setFilteredWorkshops(undefined);
    }
  }, [urlWorkshopsQuery]);

  const getDefaultSelectValueFromCategoryUrlQuery = () => {
    if (!urlWorkshopsQuery.category) {
      return undefined;
    }

    if (typeof urlWorkshopsQuery.category === "string") {
      return mapDBCategoryToOption(
        urlWorkshopsQuery.category as DB_CATEGORIES[number]
      );
    }
    return mapDBCategoriesToOptions(
      urlWorkshopsQuery.category as DB_CATEGORIES[number][]
    );
  };

  // List is usally a string when multiselect is disabled
  const mapListOfStringsToQueryObject = (
    list: string | string[],
    queryKey: "category" | "tags"
  ) => {
    let mappedUrlQuery: Record<string, string | string[]> = {};
    mappedUrlQuery[queryKey] = list;

    return mappedUrlQuery;
  };

  const setProjectsQuery = useCallback(
    (newQuery: Record<string, unknown>) => {
      // Clear any keys that's empty, e.g. if you erase the search field
      // it will be empty string, instead of the key not existing
      const query: Record<string, string> = {};
      for (const [key, value] of Object.entries(newQuery)) {
        if (value !== undefined && value !== "") {
          // @ts-ignore
          query[key] = value;
        }
      }
      void router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  useEffect(() => {
    if (selectedCategories !== undefined) {
      const updatedCategories = mapListOfStringsToQueryObject(
        selectedCategories,
        "category"
      );

      setProjectsQuery({ ...urlWorkshopsQuery, ...updatedCategories });
    }
  }, [selectedCategories]);

  return (
    <Layout
      pageTitle={
        "mentorlab | Impulse und Deep Dives für Business Transformation und Change"
      }
      pageDescription={
        "Workshops rund um Digitalisierung, Change, Agilität, Innovation, Leadership, Nachhaltigkeit, Diversity"
      }
      og={{
        currentRelativeUrlPath: "/offers",
        siteName:
          "mentorlab | Impulse und Deep Dives für Business Transformation und Change",
      }}
    >
      <PageContainer>
        <Container maxW={"7xl"} mt={10}>
          <HeroWrapper
            title={
              "Impulse und Deep Dives für Business Transformation und Change: kompakt, kompetent und kollaborativ"
            }
            subTitle={
              "Neue Wirtschaft braucht neue Beratung – Bei der Neuausrichtung deines Unternehmens auf die Herausforderungen der digitalen Welt, brauchst du mehr als Standardrezepte: Erfahrene Berater, die auf deine individuellen Bedürfnisse eingehen und dir aus ihrer Erfahrung wertvolle Impulse liefern können"
            }
            imgUrl={"/workshops.svg"}
            imgAltText={"mentorlab workshops page illustration"}
          />
          <Box mt={20}>
            <WorkshopFilter title={"Filtere nach Kategorien"}>
              {urlWorkshopsQuery.category ? (
                <CustomSelect
                  isMulti={true}
                  placeholder="Kategorien auswählen..."
                  options={categoriesAsOptions}
                  onSetUpdatedSelection={onSetSelectedCategories}
                  initialDefaultValue={
                    getDefaultSelectValueFromCategoryUrlQuery() as Option[]
                  }
                />
              ) : (
                <CustomSelect
                  isMulti={true}
                  placeholder="Kategorien auswählen..."
                  options={categoriesAsOptions}
                  onSetUpdatedSelection={onSetSelectedCategories}
                />
              )}
            </WorkshopFilter>
          </Box>

          {filteredWorkshops ? (
            <WorkshopGrid workshops={filteredWorkshops} />
          ) : (
            <WorkshopGrid workshops={allWorkshopsWithMentor} />
          )}
        </Container>
      </PageContainer>
    </Layout>
  );
};

export default Offers;

export const getStaticProps: GetStaticProps = async () => {
  const allWorkshops = await getWorkshops();
  if (!allWorkshops) {
    return {
      notFound: true,
    };
  }

  // Type assertion: We can be sure that is will return an array of workshops
  const allWorkshopsWithMentor = await workshopsWithMentor(
    allWorkshops as IWorkshopModel[]
  );
  if (!allWorkshopsWithMentor) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      allWorkshopsWithMentor,
    },
    revalidate: 10,
  };
};
